import { TextInput } from "@brexhq/component-library";
import * as React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { useFormSubmissionContext } from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { SalesPageTemplate } from "@brexhq/next/src/components/modules/LeadForm/Steps/PageTemplates/SalesPageTemplate";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import {
  FormFields,
  LeadFormValues,
  StepProps,
} from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";

import isPersonalEmail from "../../../../../utils/isPersonalEmail";
import { invalidWorkEmailErrorMessage } from "../../../../building-blocks/MarketoForm/utils";

export const Email: React.FC<StepProps & { checkWorkEmail?: boolean }> = ({
  checkWorkEmail = false,
}) => {
  const {
    submitError,
    submit,
    stepTransitionDirection,
    stateMachineCtx,
    form: {
      getValues,
      fields,
      programName,
      setProgress,
      resetField,
      clearErrors,
    },
    setSubmitError,
  } = useFormSubmissionContext();

  React.useEffect(() => {
    setProgress(0 / 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Checks if fields are valid to disable */
  const validFields = !!getValues("workEmail") && !fields.workEmail.error;

  const resetAllSubsequentFields = React.useCallback(() => {
    Object.keys(fields)
      .filter((field) => field !== FormFields.workEmail)
      .map((field) => resetField(field as FormFields));
  }, [resetField, fields]);

  const handleEmailChange = React.useCallback(
    (value: string) => {
      fields.workEmail.onChange({ value });
      clearErrors();
      setSubmitError(null);
    },
    [fields.workEmail, clearErrors, setSubmitError],
  );

  const onSubmit = React.useCallback(
    async (event) => {
      /**
       * List of fields used for this form. Most will have 1 field.
       * Type safe so the string fieldNames are based off FormValues
       */
      const fieldList: (keyof LeadFormValues)[] = ["workEmail"];

      if (getValues("workEmail") !== stateMachineCtx.workEmail) {
        resetAllSubsequentFields();
      }

      if (
        checkWorkEmail &&
        getValues("workEmail") &&
        isPersonalEmail(getValues("workEmail"))
      ) {
        setSubmitError({
          title: "Invalid email",
          message: invalidWorkEmailErrorMessage,
        });
        return;
      }

      /**
       * Handles form submission including validations.
       * Reference FormSubmissionProvider.tsx for more context
       */
      submit({
        event,
        fields: fieldList,
      });
    },
    [
      submit,
      resetAllSubsequentFields,
      stateMachineCtx,
      getValues,
      checkWorkEmail,
      setSubmitError,
    ],
  );

  return (
    <SalesPageTemplate
      title={"Enter your email"}
      step={LeadFormSteps.email}
      programName={programName}
      nextAction={{
        label: "Continue",
        loading: false,
        disabled: !validFields,
        onClick: onSubmit,
      }}
      stepTransitionDirection={stepTransitionDirection}
      error={submitError}
    >
      <>
        <Container
          width="100%"
          display="flex"
          direction={{
            LRG: "row",
            SML: "column",
          }}
          margin={{
            LRG: [0, 0, "xxsml", 0],
            MED: [0, 0, "xxsml", 0],
            SML: [0, 0, "xxsml", 0],
          }}
          gap="sml"
        >
          <div style={{ flex: 1 }}>
            <TextInput
              inputMode="email"
              size="large"
              autoComplete="email"
              {...fields.workEmail}
              error={
                fields.workEmail.error
                  ? fields.workEmail.error
                  : submitError
                  ? submitError.message
                  : null
              }
              onChange={(e) => handleEmailChange(e.value)}
            />
          </div>
        </Container>
      </>
    </SalesPageTemplate>
  );
};
